import { Extension, findChildren } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { TableMap, addColumn } from '@tiptap/pm/tables';
import { CustomSectionType } from 'api/custom-section/types';

export const AutoLastColumnTable = Extension.create({
  name: 'autoLastColumnTable',
  addOptions() {
    return {
      showEditing: true,
    };
  },
  addProseMirrorPlugins() {
    return [
      // Plugin to sync DOM colgroup after column deletions
      new Plugin({
        key: new PluginKey('syncColgroupDOM'),
        view() {
          return {
            update: (view) => {
              // Find all table elements in the DOM
              const tables = view.dom.querySelectorAll('table');

              tables.forEach((tableElement) => {
                const colgroup = tableElement.querySelector('colgroup');

                if (colgroup) {
                  // Count actual columns in the first row
                  const firstRow = tableElement.querySelector('tr');

                  if (firstRow) {
                    const actualColumns = firstRow.children.length;
                    const colgroupColumns = colgroup.children.length;

                    // If mismatch, rebuild colgroup
                    if (actualColumns !== colgroupColumns) {
                      // Remove all existing col elements
                      while (colgroup.firstChild) {
                        colgroup.removeChild(colgroup.firstChild);
                      }

                      // Add correct number of col elements
                      for (let i = 0; i < actualColumns; i++) {
                        const col = document.createElement('col');

                        col.style.minWidth = '25px';
                        colgroup.appendChild(col);
                      }
                    }
                  }
                }
              });
            },
          };
        },
      }),
      new Plugin({
        key: new PluginKey('ghostColumnTable'),
        appendTransaction: (transactions, oldState, newState) => {
          if (!this.options.showEditing) {
            return null;
          }

          if (transactions.every((tr) => !tr.docChanged)) {
            return null;
          }

          let tr = newState.tr;
          let modified = false;

          let sections = findChildren(
            newState.doc,
            (n) =>
              n.type.name === 'RL_CUSTOM_SECTION' &&
              (n.attrs.sectionType === CustomSectionType.TableDetails ||
                n.attrs.sectionType === CustomSectionType.RecordBlock),
          );

          // Collect all tables that need ghost columns
          const tablesToProcess: Array<{
            sectionPos: number;
            tablePos: number;
            tableNode: any;
            map: any;
            lastColIndex: number;
          }> = [];

          // First pass: identify all tables that need ghost columns
          sections.forEach((section) => {
            const sectionNode = newState.doc.nodeAt(section.pos);

            if (!sectionNode) {
              return;
            }

            const sectionTables = findChildren(
              sectionNode,
              (n) => n.type.name === 'table',
            );

            sectionTables.forEach((table) => {
              const tableNode = newState.doc.nodeAt(
                section.pos + table.pos + 1,
              );

              if (!tableNode || sectionNode.attrs.isTableStatic) {
                return;
              }

              const map = TableMap.get(tableNode);
              const lastColIndex = map.width - 1;
              const tablePos = section.pos + table.pos + 1;

              // Get the header cell in the last column, first row
              const lastHeaderPos = tablePos + 1 + map.map[lastColIndex];
              const lastHeaderNode = newState.doc.nodeAt(lastHeaderPos);

              const isGhost = lastHeaderNode?.attrs?.ghost === true;

              if (
                !isGhost &&
                lastHeaderNode?.attrs?.config?.dataType &&
                !tableNode.attrs.isTableStatic
              ) {
                tablesToProcess.push({
                  sectionPos: section.pos,
                  tablePos,
                  tableNode,
                  map,
                  lastColIndex,
                });
              }
            });
          });

          // Second pass: process tables in reverse order to avoid position conflicts
          // Process from last to first to avoid position shifts affecting earlier tables
          let currentTr = tr;
          let hasModifications = false;

          for (let i = tablesToProcess.length - 1; i >= 0; i--) {
            const { tablePos, tableNode, map, lastColIndex } =
              tablesToProcess[i];

            // 1. Add column at the end
            currentTr = addColumn(
              currentTr,
              { map, table: tableNode, tableStart: tablePos } as any,
              map.width,
            );

            // 2. Recompute map for the modified table
            const newTable = currentTr.doc.nodeAt(tablePos);

            if (!newTable || newTable.type.name !== 'table') {
              // eslint-disable-next-line no-console
              console.error(
                'Invalid table node at position:',
                tablePos,
                newTable,
              );
              continue;
            }

            let newMap;

            try {
              newMap = TableMap.get(newTable);
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error(
                'Error getting TableMap for newTable:',
                error,
                newTable,
              );
              continue;
            }

            // 3. Mark new column cells as ghost
            for (let row = 0; row < newMap.height; row++) {
              const cellPos =
                newMap.map[row * newMap.width + lastColIndex + 1] +
                tablePos +
                1;
              const cellNode = currentTr.doc.nodeAt(cellPos);

              if (cellNode) {
                currentTr = currentTr.setNodeMarkup(cellPos, undefined, {
                  ...cellNode.attrs,
                  ghost: true,
                });
              }
            }

            hasModifications = true;
          }

          modified = hasModifications;
          tr = currentTr;

          return modified ? tr : null;
        },
      }),
    ];
  },
});
